import React, { useState, useRef, useEffect, useCallback } from 'react';
import InputText from 'app/components/common/InputText/InputText';
import { getCodeInvalidMessage, getPhoneInvalidMessage } from 'app/libs/inputValidation';
import usePhoneCertification from 'app/hook/common/usePhoneCertification';
import { testNumber } from 'app/libs/regEx';
import LoadingBar from 'app/components/common/LoadingBar';
import dayjs from 'app/libs/dayjs';
import useIsMounted from 'app/hook/common/useIsMounted';
import { loggingException } from 'app/libs/sentry';

function InputPhoneCertification({
	onSuccess,
	onChangeExpireTime = () => {},
	isRequired = false,
	defaultPhone = undefined,
	defaultVerifiedPhone = undefined,
	defaultCodeExpireTime = null,
	bindValidationChecker = undefined,
	phoneRef = undefined,
	phoneDescriptionText = undefined,
	site = undefined,
	inputDesign = 'input',
	inputDesignClass = '',
	useLabel = true,
	btnClass = '',
	verifyInputMethod = 'basic',
	cellphoneInputStyle = { marginTop: '30px' },
	codeInputStyle = { marginTop: '30px' },
	disabled = false,
	pageType = undefined,
	labelStyle = undefined,
	verifyButtonText = '인증확인',
	onEditPhone = undefined,
	editPhoneButtonText = '휴대전화번호 수정',
	codeMaxLength = undefined,
	onFocusPhone = undefined,
	onFocusCode = undefined,
	useCodeBtnDiabled = true,
	onChangePhone = () => {},
	onChangeCode = () => {},
	phoneName = 'cellphone',
	codeName = 'code',
	onApplyPhoneCertification = () => {},
	defaultPhoneCertificationInfo = null,
	checkSendMsg = false,
	shouldResetPhone = false,
	onResetComplete = undefined,
}) {
	const isMounted = useIsMounted();
	const [loading, setLoading] = useState(false);
	const [phone, setPhone] = useState(defaultVerifiedPhone || defaultPhone || '');
	const [code, setCode] = useState('');
	const [phoneInvalidMessage, setPhoneInvalidMessage] = useState('');
	const [codeInvalidMessage, setCodeInvalidMessage] = useState('');
	const codeRef = useRef(null);
	const [verifiedPhone, setVerifiedPhone] = useState(defaultVerifiedPhone);
	const [showCodeInput, setShowCodeInput] = useState(false);
	const {
		applyPhoneCertification,
		setPhoneCertificationInfo,
		verifyPhoneCertification,
		certificationCodeExpireTime,
		certificationCodeRemainTime,
		isCertificationExpired,
		setCertificationCodeExpireTime,
	} = usePhoneCertification(pageType);

	useEffect(() => {
		setVerifiedPhone(defaultVerifiedPhone);
	}, [defaultVerifiedPhone]);

	useEffect(() => {
		setPhoneCertificationInfo(defaultPhoneCertificationInfo);
	}, [defaultPhoneCertificationInfo, setPhoneCertificationInfo]);

	useEffect(() => {
		onChangeExpireTime(certificationCodeExpireTime);
	}, [onChangeExpireTime, certificationCodeExpireTime]);

	useEffect(() => {
		if (!defaultCodeExpireTime) return;
		setCertificationCodeExpireTime(dayjs(defaultCodeExpireTime));
	}, [setCertificationCodeExpireTime, defaultCodeExpireTime]);

	useEffect(() => {
		if (isCertificationExpired) {
			setCodeInvalidMessage('인증번호 입력시간을 초과하였습니다.');
		}
	}, [isCertificationExpired]);

	// phone 초기화 처리
	useEffect(() => {
		if (shouldResetPhone) {
			setPhone('');
			setCode('');
			setVerifiedPhone('');
			setPhoneInvalidMessage('');
			setCodeInvalidMessage('');
			setShowCodeInput(false);
			setCertificationCodeExpireTime(null);
			if (onResetComplete) {
				onResetComplete();
			}
		}
	}, [shouldResetPhone, onResetComplete, setCertificationCodeExpireTime]);

	const validForm = useCallback(() => {
		const invalidPhone = getPhoneInvalidMessage(phone);
		const invalidCode = !verifiedPhone && getCodeInvalidMessage(code);

		setPhoneInvalidMessage(invalidPhone);
		setCodeInvalidMessage(invalidCode);

		if (invalidPhone) {
			return false;
		}
		if (!verifiedPhone) {
			setCodeInvalidMessage('휴대폰번호 인증을 확인해주세요.');
			return false;
		}

		return true;
	}, [code, phone, verifiedPhone]);

	useEffect(() => {
		if (bindValidationChecker) {
			// eslint-disable-next-line
			bindValidationChecker.current = validForm;
		}
	}, [bindValidationChecker, validForm]);

	const handlePhone = useCallback(
		(e) => {
			const { value } = e.target;
			if (value.length < 14 && (value === '' || testNumber(value))) {
				setPhone(e.target.value);
				if (onChangePhone) {
					onChangePhone(e);
				}
			}
		},
		[setPhone, onChangePhone],
	);
	const handleCode = useCallback(
		(e) => {
			setCode(e.target.value);
			if (onChangeCode) {
				onChangeCode(e);
			}
		},
		[setCode, onChangeCode],
	);

	const handlePhoneValidation = useCallback(
		(e) => {
			setPhoneInvalidMessage(getPhoneInvalidMessage(e.target.value));
		},
		[setPhoneInvalidMessage],
	);

	const applyCode = useCallback(async () => {
		// 20240401 휴대폰인증 어뷰징 유저 방어코드
		if (
			site === 'encarLoan' &&
			(phone === '01082423350' ||
				phone === '01052904209' ||
				phone === '01036034282' || // 테스트용 이석희 팀장
				phone === '01040628063') // 테스트용 이누리
		) {
			alert(
				'해당 정보로는 서비스 신청이 불가합니다.\n중고차론 상담신청을 원하시는 고객님께서는\n하단의 번호로 연락 부탁드립니다.\n☎ 02-754-9914',
			);
			return;
		}

		const invalidPhone = getPhoneInvalidMessage(phone);
		if (invalidPhone) {
			setPhoneInvalidMessage(invalidPhone);
			return;
		}

		setLoading(true);

		if (certificationCodeExpireTime) {
			const calcTime = dayjs().add(2, 'minutes').add(55, 'second');
			if (certificationCodeExpireTime.isAfter(calcTime)) {
				alert('이미 요청되었습니다. 잠시 후 다시 시도해주세요.');
				setLoading(false);
				return;
			}
		}

		try {
			const phoneCertificationInfo = await applyPhoneCertification(phone, site);
			setCodeInvalidMessage('');
			codeRef?.current?.focus();
			if (verifyInputMethod === 'hidden') {
				setShowCodeInput(true);
			}

			if (onApplyPhoneCertification) {
				onApplyPhoneCertification(phoneCertificationInfo);
			}
		} catch (e) {
			console.error(e);
			alert('문제가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	}, [
		certificationCodeExpireTime,
		applyPhoneCertification,
		setCodeInvalidMessage,
		codeRef,
		verifyInputMethod,
		setShowCodeInput,
		setLoading,
		phone,
		site,
		setPhoneInvalidMessage,
		onApplyPhoneCertification,
	]);

	const verifyCode = useCallback(async () => {
		const invalidCodeMessage = getCodeInvalidMessage(code);
		setCodeInvalidMessage(invalidCodeMessage);
		if (invalidCodeMessage) {
			codeRef.current.focus();
			return;
		}

		if (isCertificationExpired) {
			setCodeInvalidMessage('인증번호 입력시간을 초과하였습니다.');
			return;
		}

		try {
			setLoading(true);
			const result = await verifyPhoneCertification(code, site);
			setPhone(result.phone);
			setVerifiedPhone(result.phone);
			setCodeInvalidMessage('');
			if (verifyInputMethod === 'hidden') {
				setShowCodeInput(false);
			}
			onSuccess(result.phone, result.key);
			alert('인증되었습니다.');
		} catch (error) {
			if (error?.response?.status === 400 || (pageType === 'hs' && error?.response?.status === 401)) {
				setCodeInvalidMessage('인증번호가 일치하지 않습니다.');
				codeRef.current.focus();
			} else {
				console.error(error);
				loggingException(error);
				alert('문제가 발생했습니다.');
			}
		} finally {
			if (isMounted()) {
				setLoading(false);
			}
		}
	}, [
		pageType,
		isMounted,
		setCodeInvalidMessage,
		codeRef,
		setLoading,
		verifyPhoneCertification,
		setPhone,
		setVerifiedPhone,
		onSuccess,
		setShowCodeInput,
		code,
		site,
		isCertificationExpired,
		verifyInputMethod,
	]);

	// 인증 완료 상태에서 '휴대전화번호 수정' 클릭 → 레이어 없이 즉시 편집 가능 상태로 리셋
	const handleEditPhone = useCallback(() => {
		setVerifiedPhone('');
		setShowCodeInput(false);
		setCode('');
		setCodeInvalidMessage('');
		setCertificationCodeExpireTime(null);
		if (onEditPhone) onEditPhone();
	}, [onEditPhone, setCertificationCodeExpireTime]);

	const applyCodeKeyPress = useCallback(
		(e) => {
			if (e.key === 'Enter') {
				applyCode();
			}
		},
		[applyCode],
	);

	const verifyCodeKeyPress = useCallback(
		(e) => {
			if (e.key === 'Enter') {
				verifyCode();
			}
		},
		[verifyCode],
	);

	const checkCodeInput = () => {
		if (checkSendMsg && certificationCodeRemainTime) {
			setCodeInvalidMessage('휴대폰 인증을 진행해주세요.');
		}
	};

	const disabledApplyPhone = !!getPhoneInvalidMessage(phone);

	let phoneButtonName = '인증번호 요청';
	if (verifiedPhone) phoneButtonName = editPhoneButtonText;
	else if (certificationCodeExpireTime) phoneButtonName = '인증번호 재요청';

	return (
		<>
			<InputText
				useLabel={useLabel}
				label="휴대전화번호"
				labelStyle={labelStyle}
				type="tel"
				id="cellphone"
				name={phoneName}
				value={phone}
				inputDesign={inputDesign}
				inputDesignClass={inputDesignClass}
				placeholder="'-' 제외하고 숫자만 입력"
				onChange={handlePhone}
				onBlur={handlePhoneValidation}
				validation={!!phoneInvalidMessage}
				validationText={phoneInvalidMessage}
				ref={phoneRef}
				readOnly={!!verifiedPhone}
				required={isRequired}
				useBtn={!verifiedPhone || !!onEditPhone}
				btnDisabled={verifiedPhone ? disabled : disabledApplyPhone}
				btnName={phoneButtonName}
				btnClass={btnClass}
				onClick={verifiedPhone ? handleEditPhone : applyCode}
				onKeyPress={applyCodeKeyPress}
				style={cellphoneInputStyle}
				onFocus={onFocusPhone}
				data-enlog-dt-eventnamegroup="전화번호인증"
				disabled={disabled}
				useBtnEmptyDisabled
			/>
			{phoneDescriptionText && (
				<p
					className="fs12 fcBlack3"
					style={{ marginTop: '10px' }}
				>
					{phoneDescriptionText}
				</p>
			)}
			{((verifyInputMethod === 'basic' && !verifiedPhone) ||
				(verifyInputMethod === 'hidden' && showCodeInput) ||
				certificationCodeExpireTime) && (
				<InputText
					useLabel={useLabel}
					labelStyle={labelStyle}
					label="인증번호"
					type="customNumber"
					inputMode="numeric"
					id="code"
					name={codeName}
					value={code}
					inputDesign={inputDesign}
					inputDesignClass={inputDesignClass}
					placeholder="인증번호 입력"
					onChange={handleCode}
					validation={!!codeInvalidMessage}
					validationText={codeInvalidMessage}
					useBtn={!!certificationCodeExpireTime}
					btnName={verifyButtonText}
					readOnly={!!verifiedPhone || disabledApplyPhone}
					onClick={verifyCode}
					onKeyPress={verifyCodeKeyPress}
					onBlur={checkCodeInput}
					style={codeInputStyle}
					onFocus={onFocusCode}
					ref={codeRef}
					btnDisabled={useCodeBtnDiabled ? isCertificationExpired || !code : false}
					maxLength={codeMaxLength}
					data-enlog-dt-eventnamegroup="전화번호인증"
					btnClass={btnClass}
				/>
			)}
			{certificationCodeRemainTime && (
				<p
					className="fcMain fs13"
					style={{ marginTop: 10 }}
				>
					{parseInt(certificationCodeRemainTime.format('x'), 10) > 0 && `남은 시간 : ${certificationCodeRemainTime.format('mm:ss')}`}
				</p>
			)}

			{loading && <LoadingBar />}
		</>
	);
}

export default InputPhoneCertification;
